import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Chip,
  Paper,
  Tooltip,
  CircularProgress,
  Avatar,
  AvatarGroup,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LinkIcon from "@mui/icons-material/Link";
import { useTranslation } from "react-i18next";
import {
  useGetListOfBugsQuery,
  useUpdateBugDetailMutation,
  useGetBugDetailByBugIdQuery,
} from "@/features/bughurd/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  typeToIconMapping,
  getTypeIcon,
  getProjectIdForDetails,
  getSeverityIcon,
  relationshipTypes,
  getStatusColor,
  type RelationshipType,
} from "@/pages/Board/utils";

interface LinkedTaskItem {
  id: string;
  bugId: string;
  bugKey: string;
  title: string;
  status: string;
  type: string;
  relationshipType: RelationshipType;
  priority: string;
  assignees: any[];
}

interface Props {
  bugId: string;
}

const LinkedTask = ({ bugId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: projectIdFromParams } = useParams();
  const [updateBug, { isLoading: isUpdating }] = useUpdateBugDetailMutation();

  const {
    data: bugData,
    isLoading: isBugLoading,
    refetch: refetchBugDetails,
  } = useGetBugDetailByBugIdQuery(
    { bugId, withDetails: true },
    {
      refetchOnMountOrArgChange: true,
      skip: !bugId,
    },
  );
  const bugDetailData = bugData?.data?.decryptedData;

  const [expanded, setExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedRelationship, setSelectedRelationship] =
    useState<RelationshipType>("relates to");
  const [selectedTasks, setSelectedTasks] = useState<any[]>([]);
  const [linkedTasks, setLinkedTasks] = useState<LinkedTaskItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [allBugs, setAllBugs] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLinking, setIsLinking] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
      setAllBugs([]);
      setHasMore(true);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const projectId = getProjectIdForDetails(bugDetailData);

  // Fetch list of bugs for autocomplete with search and pagination
  const {
    data: bugsData,
    isLoading: isBugsLoading,
    isFetching: isBugsFetching,
  } = useGetListOfBugsQuery(
    {
      id: projectId,
      page: page,
      limit: 20,
      search: debouncedSearch || undefined,
    },
    {
      skip: !projectId,
      refetchOnMountOrArgChange: true,
    },
  );

  // Accumulate bugs as pages load
  useEffect(() => {
    if (bugsData?.data?.decryptedData?.items) {
      const newBugs = bugsData.data.decryptedData.items;
      const totalItems = bugsData.data.decryptedData.total || 0;

      if (page === 1) {
        setAllBugs(newBugs);
        const hasMorePages = newBugs.length < totalItems;
        setHasMore(hasMorePages);
      } else {
        setAllBugs((prev) => {
          const existingIds = new Set(prev.map((bug) => bug._id));
          const uniqueNewBugs = newBugs.filter(
            (bug: any) => !existingIds.has(bug._id),
          );
          const updatedBugs = [...prev, ...uniqueNewBugs];
          const hasMorePages = updatedBugs.length < totalItems;
          setHasMore(hasMorePages);
          return updatedBugs;
        });
      }
    }
  }, [bugsData, page]);

  const availableTasks = useMemo(() => {
    if (!allBugs || allBugs.length === 0) return [];

    // Filter out current bug and already linked bugs
    const linkedBugIds = linkedTasks?.map((task) => task.bugId);
    return allBugs.filter(
      (bug: any) => bug._id !== bugId && !linkedBugIds?.includes(bug._id),
    );
  }, [allBugs, bugId, linkedTasks]);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isBugsFetching) {
      setPage((prev) => prev + 1);
    }
  }, [hasMore, isBugsFetching, page]);

  // Sync linkedTasks with bugDetailData.linkBugs
  useEffect(() => {
    if (bugDetailData?.linkBugs && Array.isArray(bugDetailData.linkBugs)) {
      const mapTypeToRelationship = (type: string): RelationshipType => {
        if (type === "Waiting") return "is blocked by";
        if (type === "Blocked") return "blocks";
        if (type === "Clones") return "clones";
        if (type === "Cloned") return "is cloned by";
        if (type === "Duplicates") return "duplicates";
        if (type === "Duplicated") return "is duplicated by";
        return "relates to";
      };

      const parsedLinks: LinkedTaskItem[] = bugDetailData.linkBugs
        ?.map((linkBug: any) => {
          // API always returns full bug object
          const task = linkBug.bug;

          // If bug is null or undefined, skip this entry
          if (!task || !task._id) {
            return null;
          }

          return {
            id: task._id,
            bugId: task._id,
            bugKey: task.bugKey || "",
            title: task.summary || task.bugKey || "",
            status: task.bugStatus?.status || task.status || "",
            type: task.type || "",
            relationshipType: mapTypeToRelationship(linkBug.type),
            priority: task.severity || "",
            assignees: task.assignTo || [],
          };
        })
        ?.filter((link) => link !== null) as LinkedTaskItem[];

      setLinkedTasks(parsedLinks);
    } else {
      setLinkedTasks([]);
    }
  }, [bugDetailData?.linkBugs]);

  const handleAddLink = async () => {
    if (!selectedTasks || selectedTasks.length === 0) return;

    setIsLinking(true);
    try {
      // Get existing linkBugs from bugDetailData
      const existingLinkBugs = bugDetailData?.linkBugs || [];

      // Map relationship type to API type
      const mapRelationshipToType = (relationship: RelationshipType) => {
        if (relationship === "is blocked by") return "Waiting";
        if (relationship === "blocks") return "Blocked";
        if (relationship === "clones") return "Clones";
        if (relationship === "is cloned by") return "Cloned";
        if (relationship === "duplicates") return "Duplicates";
        if (relationship === "is duplicated by") return "Duplicated";
        return "Linked";
      };

      // Create new link bugs for API - send only bug ID
      const newLinkBugs = selectedTasks?.map((selectedTask) => ({
        type: mapRelationshipToType(selectedRelationship),
        bug: selectedTask._id,
      }));

      // Combine existing and new link bugs
      const allLinkBugs = [...existingLinkBugs, ...newLinkBugs];

      // Call API to update bug
      await updateBug({
        bugId: bugId,
        linkBugs: allLinkBugs,
      }).unwrap();

      // Refetch bug details to get updated linkBugs
      await refetchBugDetails();

      // Clear form state - linkedTasks will be updated by useEffect after refetch
      setSelectedTasks([]);
      setShowAddForm(false);
      setSelectedRelationship("relates to");
      setSearchTerm("");
      setDebouncedSearch("");
      setPage(1);

      toast.success(t("Linked tasks successfully"));
    } catch (error) {
      console.error("Error linking tasks:", error);
      toast.error(t("Failed to link tasks"));
    } finally {
      setIsLinking(false);
    }
  };

  const handleRemoveLink = async (linkId: string) => {
    setDeletingLinkId(linkId);
    try {
      // Get existing linkBugs from bugDetailData
      const existingLinkBugs = bugDetailData?.linkBugs || [];

      // Filter out the link we want to remove
      const updatedLinkBugs = existingLinkBugs?.filter((linkBug: any) => {
        const bugIdToCompare =
          typeof linkBug.bug === "object" ? linkBug.bug?._id : linkBug.bug;
        return bugIdToCompare !== linkId;
      });

      // Call API to update bug
      await updateBug({
        bugId: bugId,
        linkBugs: updatedLinkBugs,
      }).unwrap();

      // Refetch bug details to get updated linkBugs
      await refetchBugDetails();

      toast.success(t("Link removed successfully"));
    } catch (error) {
      console.error("Error removing link:", error);
      toast.error(t("Failed to remove link"));
    } finally {
      setDeletingLinkId(null);
    }
  };

  const handleTaskClick = (taskBugId: string) => {
    // Reset form state when navigating to another task
    setShowAddForm(false);
    setSelectedTasks([]);
    setSelectedRelationship("relates to");
    setSearchTerm("");

    navigate(
      `/defect-tracker/projects/${projectIdFromParams}/task-board?bugId=${taskBugId}`,
    );
  };

  const groupedLinks = useMemo(() => {
    const groups: { [key in RelationshipType]?: LinkedTaskItem[] } = {};
    linkedTasks.forEach((task) => {
      if (!groups[task.relationshipType]) {
        groups[task.relationshipType] = [];
      }
      groups[task.relationshipType]!.push(task);
    });
    return groups;
  }, [linkedTasks]);

  return (
    <Box sx={{ width: "100%" }} mb={2}>
      {/* Header - Hidden when used in Accordion */}
      {/* <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LinkIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          <Typography variant="subtitle2" fontWeight={600}>
            {t("Linked Tasks")} ({linkedTasks.length})
          </Typography>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ ml: 0.5 }}
          >
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box> */}

      {/* {expanded && ( */}
        <>
          {/* Loading State */}
          {isBugLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Add Link Form */}
          {!isBugLoading && showAddForm && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  {/* Dropdown on the left */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select
                      value={selectedRelationship}
                      onChange={(e) =>
                        setSelectedRelationship(
                          e.target.value as RelationshipType,
                        )
                      }
                      sx={{ textTransform: "capitalize" }}
                    >
                      {relationshipTypes?.map((type) => (
                        <MenuItem
                          key={type}
                          value={type}
                          sx={{ textTransform: "capitalize" }}
                        >
                          {t(type)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Task search on the right */}
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    options={availableTasks}
                    getOptionLabel={(option: any) =>
                      `${option.bugKey} - ${option.summary || ""}`
                    }
                    value={selectedTasks}
                    onChange={(_, newValue) => setSelectedTasks(newValue)}
                    onInputChange={(_, newInputValue) => {
                      setSearchTerm(newInputValue);
                    }}
                    loading={isBugsLoading && page === 1}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t("Search tasks...")}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isBugsLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option: any, index: number) => {
                        const typeInfo =
                          typeToIconMapping[
                            option.type as keyof typeof typeToIconMapping
                          ];
                        return (
                          <Box
                            key={option._id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {typeInfo && (
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "2px",
                                  backgroundColor: typeInfo.bgcolor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {typeInfo.icon}
                              </Box>
                            )}
                            <Chip
                              {...getTagProps({ index })}
                              label={option.bugKey}
                              size="small"
                            />
                          </Box>
                        );
                      })
                    }
                    renderOption={(props, option: any, state) => {
                      const isLastItem =
                        state.index === availableTasks.length - 1;
                      return (
                        <React.Fragment key={option._id}>
                          <li {...props}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                width: "100%",
                              }}
                            >
                              {/* Type Icon */}
                              {getTypeIcon(option.type)}
                              {/* Bug Key */}
                              <Typography
                                variant="body2"
                                sx={{ color: "#757575", mr: 0.5 }}
                              >
                                {option.bugKey}
                              </Typography>
                              {/* Summary */}
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                              >
                                {option.summary}
                              </Typography>
                            </Box>
                          </li>
                          {isLastItem && hasMore && (
                            <li
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                padding: "8px",
                                borderTop: "1px solid #e0e0e0",
                              }}
                            >
                              <Button
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLoadMore();
                                }}
                                disabled={isBugsFetching}
                                startIcon={
                                  isBugsFetching && page > 1 ? (
                                    <CircularProgress size={16} />
                                  ) : null
                                }
                                sx={{ textTransform: "none" }}
                              >
                                {isBugsFetching && page > 1
                                  ? t("Loading...")
                                  : t("Load More")}
                              </Button>
                            </li>
                          )}
                        </React.Fragment>
                      );
                    }}
                    noOptionsText={t("No tasks found")}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowAddForm(false);
                        setSelectedTasks([]);
                        setSelectedRelationship("relates to");
                        setSearchTerm("");
                        setDebouncedSearch("");
                        setPage(1);
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddLink}
                      disabled={selectedTasks.length === 0 || isLinking}
                      startIcon={
                        isLinking ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {isLinking ? t("Linking...") : t("Link")}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Linked Tasks List */}
          {!isBugLoading && linkedTasks.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {Object.entries(groupedLinks)?.map(([relationship, tasks]) => (
                <Box key={relationship}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 600,
                      mb: 0.5,
                      display: "block",
                    }}
                  >
                    {t(relationship)}
                  </Typography>
                  {tasks?.map((task, index) => (
                    <Paper
                      key={task.id}
                      elevation={0}
                      onClick={() => handleTaskClick(task.bugId)}
                      sx={{
                        p: 1,
                        border: 1,
                        borderColor: "divider",
                        cursor: "pointer",
                        borderRadius:
                          tasks.length === 1
                            ? 1
                            : index === 0
                              ? "4px 4px 0 0"
                              : index === tasks.length - 1
                                ? "0 0 4px 4px"
                                : 0,
                        mt: index === 0 ? 0 : "-1px",
                        "&:hover": {
                          backgroundColor: "action.hover",
                          "& .delete-icon": {
                            visibility: "visible",
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {/* Type Icon */}
                          {getTypeIcon(task.type)}
                          <Chip
                            label={task.bugKey}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          />
                          <Tooltip title={task.title}>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {task.title}
                            </Typography>
                          </Tooltip>
                        </Box>
                        {/* Status, Assignees, Priority section with gaps */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <Chip
                            label={task.status}
                            size="small"
                            color={getStatusColor(task.status) as any}
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              textTransform: "capitalize",
                            }}
                          />
                          {/* Assignees */}
                          {task.assignees && task.assignees.length > 0 && (
                            <AvatarGroup
                              max={3}
                              sx={{
                                "& .MuiAvatar-root": {
                                  width: 24,
                                  height: 24,
                                  fontSize: "0.7rem",
                                  border: "1px solid #fff",
                                },
                              }}
                            >
                              {task.assignees?.map(
                                (assignee: any, index: number) => (
                                  <Tooltip
                                    key={assignee._id || assignee.id || index}
                                    title={
                                      assignee.fullName || assignee.name || ""
                                    }
                                  >
                                    <Avatar
                                      src={assignee.profilePicture}
                                      alt={assignee.fullName || assignee.name}
                                      sx={{
                                        bgcolor: assignee.profilePicture
                                          ? "transparent"
                                          : "primary.main",
                                      }}
                                    >
                                      {(assignee.fullName || assignee.name)
                                        ?.charAt(0)
                                        .toUpperCase()}
                                    </Avatar>
                                  </Tooltip>
                                ),
                              )}
                            </AvatarGroup>
                          )}
                        </Box>
                        {/* Priority Icon on the right */}
                        <Tooltip title={task.priority || ""}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              "& svg": {
                                width: "100%",
                                height: "100%",
                              },
                            }}
                          >
                            {getSeverityIcon(task.priority)}
                          </Box>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveLink(task.bugId);
                          }}
                          className="delete-icon"
                          disabled={deletingLinkId === task.bugId}
                          sx={{
                            visibility: { xs: "visible", sm: "hidden" },
                            color: "error.main",
                          }}
                        >
                          {deletingLinkId === task.bugId ? (
                            <CircularProgress size={16} color="error" />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ))}
            </Box>
          )}

          {/* Add Link Task button - always show at bottom when form is not open */}
          {!isBugLoading && !showAddForm && (
            <Button
              onClick={() => setShowAddForm(true)}
              sx={{
                height: "fit-content",

                color: "#6B7888",
                textTransform: "none",
                fontSize: "13px",
                minHeight: "27px",
                p: 1,
                ml: 1.5,
                justifyContent: "flex-start",
              }}
              startIcon={<AddIcon />}
            >
              {t("Add Link Task")}
            </Button>
          )}
        </>
      {/* )} */}
    </Box>
  );
};

export default LinkedTask;
